import React, { useMemo } from "react";
import type { RefObject } from "react";
import { cn } from "@/lib/utils";
import { useWatermarkTileMetrics } from "@/hooks/useWatermarkTileMetrics";
import {
  WATERMARK_PRINT_TILE,
  buildWatermarkTileDataUrl,
} from "../utils/documentWatermarkTile";

export type DocumentWatermarkProps = {
  label: string;
  fixedColor?: boolean;
  docRootRef: RefObject<HTMLElement | null>;
  className?: string;
};

export const DocumentWatermark: React.FC<DocumentWatermarkProps> = ({
  label,
  fixedColor = false,
  docRootRef,
  className,
}) => {
  const metrics = useWatermarkTileMetrics(docRootRef);

  const backgroundImage = useMemo(() => {
    if (!metrics) {
      return undefined;
    }

    return buildWatermarkTileDataUrl({
      label,
      width: Math.round(metrics.width),
      height: Math.round(metrics.height),
    });
  }, [label, metrics]);

  // Published as a custom property for the print stylesheet to swap in: an
  // inline background-image could not be overridden from there.
  const printBackgroundImage = useMemo(
    () => buildWatermarkTileDataUrl({ label, ...WATERMARK_PRINT_TILE }),
    [label]
  );

  if (!metrics || !backgroundImage) {
    return null;
  }

  return (
    <div
      aria-hidden
      className={cn(
        "doc-watermark select-none",
        fixedColor ? "text-black/15 dark:text-black/15" : "text-foreground/15",
        className
      )}
      style={
        {
          backgroundImage,
          "--watermark-print-image": printBackgroundImage,
        } as React.CSSProperties
      }
    />
  );
};
